import React, { useContext, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { AppDivisionContext } from '../../../contexts/AppDivisionContext';
import ConfirmationModal from '../../Shared/ConfirmationModal/ConfirmationModal';
import { assetStatusesApi } from '../../../utils/api/asset';
import {
  ProjectAsset,
  projectAssetsApi,
} from '../../../utils/api/projectAsset';
import { removalWarning } from './AssetChecks';

interface IAssetStatusSelectProps {
  projectId: number;
  inspectionId: number;
  asset: ProjectAsset;
  disabled?: boolean;
  onChanged?: () => void;
}

/** Status picker for an asset inside an inspection; saves on change. */
const AssetStatusSelect: React.FC<IAssetStatusSelectProps> = ({
  projectId,
  inspectionId,
  asset,
  disabled,
  onChanged,
}) => {
  const { appDivisionId } = useContext(AppDivisionContext);
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [pendingRemoval, setPendingRemoval] = useState<number | null>(null);
  const divisionId = appDivisionId || 0;
  const statusesQuery = useQuery(['assetStatuses', divisionId], () =>
    assetStatusesApi.index(divisionId),
  );

  const statuses =
    statusesQuery.data || (asset.asset_status ? [asset.asset_status] : []);
  const color = asset.asset_status?.color || '#6B7280';

  const change = async (statusId: number) => {
    setSaving(true);
    try {
      await projectAssetsApi.setStatus(projectId, asset.id, {
        asset_status_id: statusId,
        inspection_id: inspectionId,
      });
      // Prefix key: refreshes both the inspection and the project Assets tabs.
      await queryClient.invalidateQueries(['projectAssets', projectId]);
      onChanged?.();
    } catch {
      toast.error('Failed to change asset status.');
    } finally {
      setSaving(false);
    }
  };

  const select = (statusId: number) => {
    const isRemoval = statuses.find((s) => s.id === statusId)?.is_removal;

    if (isRemoval && !asset.archived_at) setPendingRemoval(statusId);
    else change(statusId);
  };

  return (
    <>
      <select
        className='asset-section__status asset-section__status-select'
        aria-label='Asset status'
        value={asset.asset_status_id}
        disabled={disabled || saving}
        style={{ backgroundColor: `${color}1d`, color }}
        onChange={(e) => select(Number(e.target.value))}
      >
        {statuses.map((status) => (
          <option key={status.id} value={status.id}>
            {status.name}
          </option>
        ))}
      </select>
      <ConfirmationModal
        show={pendingRemoval !== null}
        title='Archive asset?'
        subTitle={removalWarning(asset)}
        buttonText='Archive Asset'
        handleConfirm={() => {
          change(pendingRemoval!);
          setPendingRemoval(null);
        }}
        handleClose={() => setPendingRemoval(null)}
      />
    </>
  );
};

export default AssetStatusSelect;
